import { useCallback, useMemo, useState } from 'react';

import BottomSheet from '@perpay-web/components/base/BottomSheet/BottomSheet';
import Combobox from '@perpay-web/components/base/Combobox/Combobox';
import { useAnimatedModal } from '@perpay-web/components/base/Modal/useAnimatedModal';
import { useModal } from '@perpay-web/components/base/Modal/useModal';
import CardApplicationSSIModal from '@perpay-web/fintech/components/composite/CardApplicationEmploymentDetails/CardApplicationSSIModal';
import CompanySelfOnboard from '@perpay-web/fintech/components/composite/CompanySelfOnboardCardSignupMVP/CompanySelfOnboardCardSignupMVP';
import { useMountAndUnmount } from '@perpay-web/hooks/useMountAndUnmount';
import { getClassName } from '@perpay-web/utils/domUtils';
import {
    EndToEndTestIdProvider,
    getTestIdProps,
} from '@perpay-web/utils/endToEndTestIds';

import styles from './CompanyPickerV2.scss';

const MAX_EMPLOYERS_TO_DISPLAY = 4;

const CompanyPickerV2 = ({
    error = '',
    topMatches,
    matches,
    suggestedMatches,
    initialValue = '',
    isLoadingResults,
    showAddOption = true,
    possibleMatches,
    hasLoadedResults,
    onInputValueChange,
    onSelect,
    onSubmitSelfOnboardEmployer,
    onUnmount,
    isMarketplaceSignup = false,
    placeholder = 'Employer',
    isSSIExemption = false,
    onSSIModalMount,
    onSSIModalChangeEmployer,
    onSSIModalContinue,
    ...rest
}) => {
    useMountAndUnmount(() => onUnmount);

    const [isSSIModalOpen, setIsSSIModalOpen] = useState(false);
    const [inputValue, setInputValue] = useState(initialValue);
    const [selfOnboardingCompanyName, setSelfOnboardingCompanyName] =
        useState('');
    const [submittedSelfOnboardEmployer, setSubmittedSelfOnboardEmployer] =
        useState(false);

    const allItems = [...topMatches, ...matches, ...suggestedMatches].slice(
        0,
        MAX_EMPLOYERS_TO_DISPLAY,
    );
    const itemsWithAddMoreOption = [...allItems, { text: inputValue }];

    const shouldShowComboboxOptions = useMemo(() => {
        if (submittedSelfOnboardEmployer) {
            return false;
        }

        // We only perform a search if the user types more than 3 characters, so
        // We should only show the combobox options under the following conditions:

        // 1. If there are exactly three characters, show only if there are company results
        if (inputValue.length === 3) {
            return (
                topMatches.length + matches.length + suggestedMatches.length > 0
            );
        }

        // 2. The input value is 3 or more characters
        if (inputValue.length > 3) {
            return true;
        }

        // 3. If there are less than 3 characters, hide
        return false;
    }, [
        inputValue.length,
        matches.length,
        suggestedMatches.length,
        topMatches.length,
        submittedSelfOnboardEmployer,
    ]);

    const onInputValueChangeInternal = useCallback(
        (value) => {
            if (submittedSelfOnboardEmployer) {
                setSubmittedSelfOnboardEmployer(false);
            }
            setInputValue(value);
            onInputValueChange(value);
        },
        [onInputValueChange, submittedSelfOnboardEmployer],
    );

    const { isModalOpen, setIsModalOpen } = useModal();
    const {
        isAnimatedModalOpen,
        setIsAnimatedModalOpen,
        animatedModalClassName,
    } = useAnimatedModal({ isModalOpen, setIsModalOpen });

    const onSelectSelfOnboarding = useCallback(() => {
        setSelfOnboardingCompanyName(inputValue);
        setIsAnimatedModalOpen(true);
    }, [inputValue, setIsAnimatedModalOpen]);

    const closeSelfOnboarding = useCallback(() => {
        setIsAnimatedModalOpen(false);
    }, [setIsAnimatedModalOpen]);

    const showEmptyResults = useMemo(
        () =>
            inputValue.length >= 3 &&
            ((hasLoadedResults && possibleMatches === 0) || !isLoadingResults),
        [
            hasLoadedResults,
            inputValue.length,
            isLoadingResults,
            possibleMatches,
        ],
    );
    const handleSSIModalChangeEmployer = () => {
        setInputValue('');
        onSelect({
            company: '',
            uuid: '',
            isGigEconomyCompany: false,
            hasPerpaySplitPayroll: false,
            isCardEligibleSsi: false,
        });
        setIsSSIModalOpen(false);
        onSSIModalChangeEmployer();
    };

    const handleSSIModalContinue = () => {
        setIsSSIModalOpen(false);
        onSSIModalContinue();
    };

    const onSelectInternal = useCallback(
        (text) => {
            const itemToSelect = allItems.find((item) => item.text === text);
            setInputValue(text);

            if (!itemToSelect) {
                onSelectSelfOnboarding();
                return;
            }

            if (itemToSelect) {
                onSelect({
                    company: itemToSelect.text,
                    uuid: itemToSelect.uuid,
                    isGigEconomyCompany: itemToSelect.isGigEconomyCompany,
                    hasPerpaySplitPayroll: itemToSelect.hasPerpaySplitPayroll,
                    isCardEligibleSsi: itemToSelect.isCardEligibleSsi,
                });
            }

            if (itemToSelect.isCardEligibleSsi && isSSIExemption) {
                setIsSSIModalOpen(true);
            }
        },
        [allItems, onSelect, onSelectSelfOnboarding, isSSIExemption],
    );

    const renderItem = (item, renderProps, index) => {
        const { highlightedIndex, onSelect: onSelectItem } = renderProps;
        const userCount =
            item.userCount >= 25 ? item.userCount.toLocaleString('en-US') : '';

        return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
                className={getClassName(
                    styles.item,
                    index === highlightedIndex && styles.highlighted,
                )}
                key={item.text}
                {...getTestIdProps(`company-combo-box-name-${index}`)}
                role='option'
                aria-selected={item ? item.name === highlightedIndex : null}
                onClick={() => onSelectItem(item.text)}
            >
                <div className={styles['company-icon']} />
                <div className={styles.item__text}>
                    <div className={styles.item__text__name}>{item.text}</div>
                    {userCount ? (
                        <span className={styles['user-count']}>
                            {userCount}
                            <span className={styles.item__text__subtext}>
                                {' '}
                                {item.text} employees have joined Perpay
                            </span>
                        </span>
                    ) : null}
                </div>
            </li>
        );
    };

    const shouldRenderAddOption = showEmptyResults && showAddOption;

    const maybeRenderEmptyResults = (renderProps) => {
        const { highlightedIndex } = renderProps;

        return shouldRenderAddOption ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
                className={getClassName(
                    styles.item,
                    highlightedIndex === allItems.length && styles.highlighted,
                )}
                key='company-combo-box-add-more'
                {...getTestIdProps(`company-combo-box-name-${allItems.length}`)}
                role='option'
                aria-selected={highlightedIndex === allItems.length}
                onClick={onSelectSelfOnboarding}
            >
                <div className={styles['add-company-icon']} />
                <div className={styles.item__text}>
                    <div className={styles.item__text__name}>
                        Don&apos;t see your company?
                    </div>
                    <div className={styles['add-company']}>
                        Click here to add your company &ldquo;
                        {inputValue}&rdquo;
                    </div>
                </div>
            </li>
        ) : null;
    };

    const onSubmitSelfOnboardEmployerCb = useCallback(
        (value) => {
            setSubmittedSelfOnboardEmployer(true);
            setInputValue(value.displayName);
            setIsAnimatedModalOpen(false);
            onSubmitSelfOnboardEmployer(value);
        },
        [setIsAnimatedModalOpen, onSubmitSelfOnboardEmployer],
    );

    return (
        <div>
            <EndToEndTestIdProvider testId='company-combo-box'>
                <Combobox
                    id='company-name'
                    name='selectedCompany'
                    value={inputValue}
                    itemCount={
                        shouldRenderAddOption
                            ? allItems.length + 1
                            : allItems.length
                    }
                    placeholder={placeholder}
                    label='Employer'
                    onSelect={onSelectInternal}
                    onInputValueChange={onInputValueChangeInternal}
                    getValueAtIndex={(index) => itemsWithAddMoreOption[index]}
                    loading={isLoadingResults}
                    {...rest}
                >
                    {(renderProps) =>
                        shouldShowComboboxOptions && (
                            <ul className={styles.list}>
                                {allItems.map((item, index) =>
                                    renderItem(item, renderProps, index),
                                )}
                                {maybeRenderEmptyResults(renderProps)}
                            </ul>
                        )
                    }
                </Combobox>
                {isAnimatedModalOpen ? (
                    <BottomSheet
                        onClose={closeSelfOnboarding}
                        className={getClassName(
                            styles['modal-container'],
                            animatedModalClassName,
                        )}
                    >
                        <CompanySelfOnboard
                            selfOnboardingInitialValue={
                                selfOnboardingCompanyName
                            }
                            onSubmitSelfOnboardEmployer={
                                onSubmitSelfOnboardEmployerCb
                            }
                            isMarketplaceSignup={isMarketplaceSignup}
                        />
                    </BottomSheet>
                ) : null}
            </EndToEndTestIdProvider>
            {error ? (
                <p className={styles.error}>An error occurred: {error}</p>
            ) : null}
            {isSSIModalOpen ? (
                <CardApplicationSSIModal
                    onChangeEmployer={handleSSIModalChangeEmployer}
                    onContinue={handleSSIModalContinue}
                    onMount={onSSIModalMount}
                />
            ) : null}
        </div>
    );
};

export default CompanyPickerV2;
